import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  HStack,
  IconButton,
  Input,
  Text,
  useDisclosure,
  VStack,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Field, RuleGroupType, QueryBuilder } from 'react-querybuilder';
import { useParams } from 'react-router-dom';
import { v4 as uuidV4 } from 'uuid';
import { z } from 'zod';

import { date as commonDate } from '@finley-technologies/common';

import { AthenaTableGQLType } from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  ChartDateRange,
  DateRange,
  ReceivablesTableColumnDef,
  queryBuilderDefaultQuery,
  QueryFilter,
  useCreditFacilityQueryParams,
  useCreditFacilityQueryFilters,
} from './utils';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import {
  athenaTablePropertyTypeToQueryBuilderType,
  formatAthenaSqlString,
  generateFieldConfig,
  validateQuery,
} from 'packages/common-frontend/components/QueryBuilder';
import { useGetCreditFacilityQuery } from 'packages/credit-facility/requests/requests.gql.generated';
import { FullDateRangePicker } from '~/packages/common-frontend/components/DateRangePicker';

// Types
const RouteParamsStruct = z.object({
  creditFacilityId: z.string({ message: 'Missing URL parameter' }),
});

// Fragments
type QueryFilterButtonProps = {
  availableFields: Field[];
  buttonLabel: string;
  buttonColor?: string;
  headerLabel: string;
  icon: React.ReactElement;
  initialQueryFilter?: QueryFilter;
  isIconButton?: boolean;
  helperText?: string;
  upsertQueryFilter: (input: { queryFilter: QueryFilter }) => QueryFilter[];
  receivablesTableSchema: AthenaTableGQLType;
};
const QueryFilterButton = ({
  availableFields,
  icon,
  initialQueryFilter,
  headerLabel,
  buttonLabel,
  buttonColor,
  helperText,
  isIconButton = false,
  upsertQueryFilter,
  receivablesTableSchema,
}: QueryFilterButtonProps) => {
  const [query, setQuery] = useState<RuleGroupType>(
    initialQueryFilter?.query || queryBuilderDefaultQuery,
  );
  const [filterName, setFilterName] = useState<string>(
    initialQueryFilter?.filterName || 'Filter',
  );

  const [, setIsError] = useState<boolean>(false);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const clearState = useCallback(() => {
    setFilterName(initialQueryFilter?.filterName || 'Filter');
    setQuery(initialQueryFilter?.query || queryBuilderDefaultQuery);
  }, [initialQueryFilter]);

  // stops button click from propagating and activating card
  const onClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      onOpen();
      event.stopPropagation();
    },
    [onOpen],
  );

  /**
   * Resets the filter name and query to their initial values on close/ cancel
   */
  const onCancel = useCallback(() => {
    clearState();
    onClose();
  }, [clearState, onClose]);

  const onSave = useCallback(async () => {
    upsertQueryFilter({
      queryFilter: {
        id: initialQueryFilter ? initialQueryFilter.id : uuidV4(),
        query,
        filterName,
        // @jendevelops Do we want to default to active when adding a new filter?
        // do we want to have an active/inactive toggle on the add/ edit filter modal?
        isActive: initialQueryFilter ? initialQueryFilter.isActive : true,
      },
    });
    clearState();
    onClose();
  }, [
    upsertQueryFilter,
    query,
    filterName,
    initialQueryFilter,
    clearState,
    onClose,
  ]);

  return (
    <>
      {isIconButton ? (
        <IconButton
          aria-label={buttonLabel}
          color={buttonColor}
          onClick={onClick}
          variant="ghost"
          icon={icon}
        >
          {icon}
        </IconButton>
      ) : (
        <Button
          alignSelf="flex-end"
          variant="outline"
          colorScheme={buttonColor}
          leftIcon={icon}
          onClick={onClick}
        >
          {buttonLabel}
        </Button>
      )}
      <Modal isOpen={isOpen} onClose={onCancel} size="5xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{headerLabel}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <HStack justifyContent="space-between" mb="24px">
              <Text>Filter Name</Text>
              <Input
                type="text"
                value={filterName}
                textAlign="left"
                onChange={(e) => setFilterName(e.target.value)}
                variant="outline"
                colorScheme="gray"
                width="sm"
              />
            </HStack>
            {helperText && (
              <Text color="gray" mb="24px">
                {helperText}
              </Text>
            )}
            <Box
              w="100%"
              borderRadius="8px"
              bg={colors.gray[200]}
              p="8px 24px"
              mb="24px"
            >
              <Text>
                {formatAthenaSqlString(
                  query,
                  receivablesTableSchema.properties,
                ) || 'The formula will be displayed here.'}
              </Text>
            </Box>

            <QueryBuilder
              fields={availableFields}
              query={query}
              onQueryChange={(newQuery) => {
                const validationResult = validateQuery(newQuery);
                setQuery(newQuery);
                setIsError(Boolean(validationResult));
              }}
              addRuleToNewGroups
              showCloneButtons
              showNotToggle
              showCombinatorsBetweenRules
            />
          </ModalBody>
          <ModalFooter>
            <HStack spacing="32px">
              <Button colorScheme="aurora" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button variant="solid" colorScheme="aurora" onClick={onSave}>
                Save Filter
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

type AddQueryFilterButtonProps = {
  availableFields: Field[];
  helperText: string;
  upsertQueryFilter: (input: { queryFilter: QueryFilter }) => QueryFilter[];
  receivablesTableSchema: AthenaTableGQLType;
};
const AddQueryFilterButton = ({
  availableFields,
  helperText,
  upsertQueryFilter,
  receivablesTableSchema,
}: AddQueryFilterButtonProps) => (
  <QueryFilterButton
    availableFields={availableFields}
    headerLabel="Add Filter"
    buttonLabel="Add Filter"
    buttonColor="aurora"
    icon={<Icon icon="plus" />}
    helperText={helperText}
    upsertQueryFilter={upsertQueryFilter}
    receivablesTableSchema={receivablesTableSchema}
  />
);

type EditQueryFilterButtonProps = {
  availableFields: Field[];
  initialQueryFilter: QueryFilter;
  upsertQueryFilter: (input: { queryFilter: QueryFilter }) => QueryFilter[];
  receivablesTableSchema: AthenaTableGQLType;
};
const EditQueryFilterButton = ({
  availableFields,
  initialQueryFilter,
  upsertQueryFilter,
  receivablesTableSchema,
}: EditQueryFilterButtonProps) => (
  <QueryFilterButton
    isIconButton
    availableFields={availableFields}
    buttonLabel="Edit Filter"
    headerLabel="Edit Filter"
    buttonColor="gray.500"
    icon={<Icon icon="edit" />}
    initialQueryFilter={initialQueryFilter}
    upsertQueryFilter={upsertQueryFilter}
    receivablesTableSchema={receivablesTableSchema}
  />
);

type DeleteQueryFilterButtonProps = {
  organizationId: string;
  queryFilter: QueryFilter;
};
const DeleteQueryFilterButton = ({
  organizationId,
  queryFilter,
}: DeleteQueryFilterButtonProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const queryParams = useCreditFacilityQueryParams();
  const routeParams = useParams();
  const { creditFacilityId } = RouteParamsStruct.parse(
    queryParams.creditFacilityId ? queryParams : routeParams,
  );

  const { deleteQueryFilter } = useCreditFacilityQueryFilters({
    organizationId,
    creditFacilityId,
  });
  const cancelRef = useRef(null);
  // stops button click from propagating and activating card
  const onClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      onOpen();
      event.stopPropagation();
    },
    [onOpen],
  );
  const onDelete = useCallback(() => {
    deleteQueryFilter({ queryFilter });
  }, [deleteQueryFilter, queryFilter]);
  return (
    <>
      <IconButton
        aria-label="delete"
        color="gray.500"
        variant="ghost"
        onClick={onClick}
      >
        <Icon icon="trash" />
      </IconButton>
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Filter
            </AlertDialogHeader>

            <AlertDialogBody>
              {`Are you sure you want to delete ${queryFilter.filterName}?`}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="aurora" onClick={onDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

type FilterCardProps = {
  organizationId: string;
  availableFields: Field[];
  queryFilter: QueryFilter;
  upsertQueryFilter: (input: { queryFilter: QueryFilter }) => QueryFilter[];
  toggleIsActivateLocalState: (input: { queryFilter: QueryFilter }) => void;
  receivablesTableSchema: AthenaTableGQLType;
};
const FilterCard = ({
  organizationId,
  availableFields,
  queryFilter,
  upsertQueryFilter,
  toggleIsActivateLocalState,
  receivablesTableSchema,
}: FilterCardProps) => {
  const { filterName, query, isActive } = queryFilter;

  const onSetActive = useCallback(() => {
    toggleIsActivateLocalState({ queryFilter });
  }, [queryFilter, toggleIsActivateLocalState]);

  return (
    <VStack
      w="320px"
      p="16px"
      spacing="8px"
      textAlign="left"
      borderRadius="8px"
      border={isActive ? '2px' : '1px'}
      borderColor={isActive ? 'aurora' : 'gray.100'}
      bgColor={isActive ? 'millet.50' : 'none'}
      alignItems="flex-start"
      alignSelf="stretch"
      flexGrow={0}
      flexShrink={0}
      onClick={onSetActive}
      cursor="pointer"
    >
      <Text fontWeight="bold" size="larger">
        {filterName}
      </Text>
      <Text color="gray" overflow="auto">
        {formatAthenaSqlString(query, receivablesTableSchema.properties) ||
          'The formula will be displayed here.'}
      </Text>
      <HStack w="100%" flexGrow={1} alignItems="flex-end">
        <EditQueryFilterButton
          availableFields={availableFields}
          initialQueryFilter={queryFilter}
          upsertQueryFilter={upsertQueryFilter}
          receivablesTableSchema={receivablesTableSchema}
        />
        <DeleteQueryFilterButton
          organizationId={organizationId}
          queryFilter={queryFilter}
        />
      </HStack>
    </VStack>
  );
};

// Exports
type FilterDrawerProps = {
  organizationId: string;
  creditFacilityId: string;
  columnDefs: ReceivablesTableColumnDef[];
  initialDateRangeFilter: DateRange;
  onSetDateRange: (args: DateRange) => void;
  receivablesTableSchema: AthenaTableGQLType;
  refetchActiveQueryFilters: () => void;
};
export const FilterDrawer = ({
  organizationId,
  creditFacilityId,
  columnDefs,
  initialDateRangeFilter,
  onSetDateRange,
  receivablesTableSchema,
  refetchActiveQueryFilters,
}: FilterDrawerProps) => {
  const {
    queryFilters,
    toggleIsActivateLocalState,
    upsertQueryFilter,
    saveLocalStateFiltersToLocalStorage,
    deactivateAllLocalStateQueryFilters,
  } = useCreditFacilityQueryFilters({ organizationId, creditFacilityId });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef(null);

  // temp for this facility
  const { data: creditFacilityData } = useGetCreditFacilityQuery({
    variables: {
      creditFacilityId,
    },
  });
  const isCreditGenieFortress1Facility =
    creditFacilityData?.creditFacility.slug === 'creditGenieFortress1';
  const dateThreeMonthsFromEnd = commonDate.addMonths(
    initialDateRangeFilter.dateEnd,
    -3,
  );

  const [dateRangeFilter, setDateRangeFilter] = useState<ChartDateRange>({
    chartDataStartDate: commonDate.fullDateStrToDate(
      isCreditGenieFortress1Facility
        ? dateThreeMonthsFromEnd
        : initialDateRangeFilter.dateStart,
    ),
    chartDataEndDate: commonDate.fullDateStrToDate(
      initialDateRangeFilter.dateEnd,
    ),
  });

  const availableFields = useMemo<Field[]>(
    () =>
      columnDefs.map(({ header, meta: { key, dataType } }) =>
        generateFieldConfig({
          name: key,
          label: header,
          fieldType: athenaTablePropertyTypeToQueryBuilderType(dataType),
        }),
      ),
    [columnDefs],
  );

  const onSelectDateRange = (newDateRange: [Date | null, Date | null]) => {
    const [newStartDate, newEndDate] = newDateRange;

    if (newStartDate && newEndDate) {
      setDateRangeFilter({
        chartDataStartDate: newStartDate,
        chartDataEndDate: newEndDate,
      });
    }

    // if dates cleared, pass in the initial reporting date conditions
    if (!newStartDate && !newEndDate) {
      setDateRangeFilter({
        chartDataStartDate: new Date(initialDateRangeFilter.dateStart),
        chartDataEndDate: new Date(initialDateRangeFilter.dateEnd),
      });
    }
  };

  const onClearAll = useCallback(() => {
    // @jendevelops [receivables-dashboard TODO]: clear local date range state
    deactivateAllLocalStateQueryFilters();
  }, [deactivateAllLocalStateQueryFilters]);

  const onApplyFilters = useCallback(() => {
    saveLocalStateFiltersToLocalStorage();
    refetchActiveQueryFilters();

    const newStartDate = commonDate.dateToFullDateString(
      dateRangeFilter.chartDataStartDate,
    );
    const newEndDate = commonDate.dateToFullDateString(
      dateRangeFilter.chartDataEndDate,
    );
    if (
      initialDateRangeFilter.dateStart !== newStartDate ||
      initialDateRangeFilter.dateEnd !== newEndDate
    ) {
      onSetDateRange({
        dateStart: commonDate.dateToFullDateString(
          dateRangeFilter.chartDataStartDate,
        ),
        dateEnd: commonDate.dateToFullDateString(
          dateRangeFilter.chartDataEndDate,
        ),
      });
    }
    onClose();
  }, [
    dateRangeFilter.chartDataEndDate,
    dateRangeFilter.chartDataStartDate,
    initialDateRangeFilter.dateEnd,
    initialDateRangeFilter.dateStart,
    onClose,
    onSetDateRange,
    refetchActiveQueryFilters,
    saveLocalStateFiltersToLocalStorage,
  ]);

  return (
    <>
      <Button
        variant="secondary-muted"
        alignSelf="flex-end"
        leftIcon={<Icon icon="sliders" />}
        onClick={onOpen}
        color="gray.800"
      >
        Filters
      </Button>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={onClose}
        finalFocusRef={btnRef}
        size="xl"
      >
        <DrawerOverlay />
        <DrawerContent p="32px">
          <DrawerCloseButton />
          <DrawerHeader fontSize="32px">Filters</DrawerHeader>
          <Divider />

          <DrawerBody>
            <Text fontSize="20px" fontWeight="bold" mb="8px">
              Date
            </Text>
            <Text color="gray" mb="24px">
              Select the date range you want to use for filtering.
            </Text>
            <HStack w="100%" spacing="40px">
              <FullDateRangePicker
                onSelectDateRange={onSelectDateRange}
                currentDateRange={{
                  startDate: dateRangeFilter.chartDataStartDate,
                  endDate: dateRangeFilter.chartDataEndDate,
                }}
                w="100%"
              />
            </HStack>

            <Divider p="32px 0" />

            <HStack w="100%" pb="24px">
              <VStack flexGrow={1} alignItems="flex-start">
                <Text fontSize="20px" fontWeight="bold" pt="32px">
                  Saved Filters
                </Text>
                <Text color="gray">
                  Choose the card(s) you want to use for filtering
                </Text>
              </VStack>
              <AddQueryFilterButton
                availableFields={availableFields}
                helperText="Select the base field property to add"
                upsertQueryFilter={upsertQueryFilter}
                receivablesTableSchema={receivablesTableSchema}
              />
            </HStack>
            <HStack spacing="16px" overflowX="auto" p="16px 0">
              {queryFilters.map((queryFilter) => (
                <FilterCard
                  key={queryFilter.id}
                  organizationId={organizationId}
                  availableFields={availableFields}
                  queryFilter={queryFilter}
                  toggleIsActivateLocalState={toggleIsActivateLocalState}
                  upsertQueryFilter={upsertQueryFilter}
                  receivablesTableSchema={receivablesTableSchema}
                />
              ))}
            </HStack>
          </DrawerBody>

          <DrawerFooter justifyContent="space-between">
            <Button variant="outline" mr={3} onClick={onClearAll}>
              Clear All
            </Button>
            <Button colorScheme="aurora" onClick={onApplyFilters}>
              Apply Filters
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
};
